import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Users, Calendar, Music, Heart, Mic } from "lucide-react";
import { fetchCourses, fetchEvents, fetchNews } from "../lib/api";
import { SCHOOL } from "../lib/site";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/0e52844b-b1e4-41c2-bf02-225b3dd24c51/images/1f3a4eda99a8cd8a117d1c51390ebbcbafbd8dddb925ac27475ef707438f50ff.png";

export default function Home() {
  const [courses, setCourses] = useState([]);
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    fetchCourses().then(setCourses).catch(() => {});
    fetchEvents().then(setEvents).catch(() => {});
    fetchNews().then(setNews).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-[88vh] flex items-end overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(28,8,16,0.55) 0%, rgba(163,19,58,0.85) 100%), url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-[1400px] w-full mx-auto px-6 md:px-10 lg:px-14 py-20 text-[#FBFBF9] relative z-10">
          <div className="max-w-3xl fade-up">
            <div className="text-[11px] tracking-eyebrow text-[#FBFBF9]/85 mb-6">
              Trinity College London · Centre {SCHOOL.trinityCentreId}
            </div>
            <h1 className="font-serif-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight font-medium mb-8 fade-up fade-up-delay-1">
              L'arte della musica,<br />
              <em className="italic font-light">l'eccellenza dello studio.</em>
            </h1>
            <p className="text-lg sm:text-xl max-w-xl opacity-90 leading-relaxed fade-up fade-up-delay-2">
              Trinity Musical School di Amici per la Musica APS — scuola di musica
              e spettacolo certificata, dove la tradizione classica incontra
              la passione contemporanea.
            </p>
            <div className="flex flex-wrap gap-4 mt-10 fade-up fade-up-delay-3">
              <Link
                to="/iscrizione"
                data-testid="hero-cta-primary"
                className="bg-[#FBFBF9] text-[#A3133A] px-8 py-4 text-[12px] tracking-eyebrow inline-flex items-center gap-2 rounded-sm hover:bg-white transition-all"
              >
                Iscriviti ai corsi <ArrowRight size={14} />
              </Link>
              <Link
                to="/corsi"
                data-testid="hero-cta-secondary"
                className="border border-[#FBFBF9]/60 text-[#FBFBF9] px-8 py-4 text-[12px] tracking-eyebrow inline-flex items-center gap-2 rounded-sm hover:bg-white/10 transition-all"
              >
                Esplora i corsi
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto" data-testid="home-pillars">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-5">L'evoluzione</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1C1C1C] leading-tight mb-6">
              Dell'eccellenza artistica<br />e culturale.
            </h2>
            <p className="text-[#5C5C5C] leading-relaxed mb-8">
              Da oltre un decennio, Amici per la Musica APS forma musicisti e artisti
              attraverso un percorso didattico rigoroso che unisce la tradizione del
              Conservatorio alla contemporaneità delle performing arts.
            </p>
            <Link to="/chi-siamo" data-testid="home-pillars-cta" className="text-[#A3133A] tracking-eyebrow text-[12px] link-underline inline-flex items-center gap-2">
              Scopri la nostra storia <ArrowRight size={14} />
            </Link>
          </div>

          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#A3133A]/15">
            {[
              { icon: Award, label: "Centro Esami Trinity", value: "71607" },
              { icon: Users, label: "Allievi attivi", value: "200+" },
              { icon: Music, label: "Sedi didattiche", value: "3" },
              { icon: Calendar, label: "Eventi all'anno", value: "20+" },
            ].map((s, i) => (
              <div key={s.label} className="bg-[#FBFBF9] p-8">
                <s.icon size={22} className="text-[#A3133A] mb-4" />
                <div className="font-serif-display text-4xl text-[#1C1C1C] mb-1">{s.value}</div>
                <div className="text-[11px] tracking-eyebrow text-[#5C5C5C]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COURSES PREVIEW */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#A3133A] text-[#FBFBF9] relative grain" data-testid="home-courses">
        <div className="max-w-[1400px] mx-auto relative z-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
            <div>
              <div className="text-[11px] tracking-eyebrow opacity-80 mb-4">Discipline</div>
              <h2 className="font-serif-display text-4xl sm:text-6xl leading-tight">
                Un percorso<br /><em className="italic font-light">per ogni voce.</em>
              </h2>
            </div>
            <Link to="/corsi" data-testid="home-courses-all" className="text-[12px] tracking-eyebrow border border-white/40 px-6 py-3 rounded-sm hover:bg-white hover:text-[#A3133A] transition self-start md:self-auto">
              Tutti i corsi
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/15">
            {courses.slice(0, 6).map((c) => (
              <Link
                to="/corsi"
                key={c.id}
                data-testid={`home-course-${c.id}`}
                className="bg-[#A3133A] p-8 hover:bg-[#8B0F2F] transition-all group"
              >
                <div className="text-[10px] tracking-eyebrow opacity-70 mb-3">{c.categoria}</div>
                <h3 className="font-serif-display text-3xl mb-3">{c.nome}</h3>
                <p className="text-sm opacity-85 line-clamp-3">{c.descrizione}</p>
                <div className="mt-6 text-[12px] tracking-eyebrow inline-flex items-center gap-2 opacity-90 group-hover:opacity-100">
                  Scopri <ArrowRight size={12} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS PREVIEW */}
      <section className="py-24 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto" data-testid="home-events">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-4">Calendario</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1C1C1C] leading-tight mb-6">
              Prossimi appuntamenti
            </h2>
            <p className="text-[#5C5C5C] leading-relaxed mb-6">
              Saggi, concerti, masterclass ed esami Trinity College London.
              La vita musicale della scuola, sempre aperta al pubblico.
            </p>
            <Link to="/eventi" data-testid="home-events-all" className="text-[#A3133A] tracking-eyebrow text-[12px] link-underline inline-flex items-center gap-2">
              Vedi calendario completo <ArrowRight size={14} />
            </Link>
          </div>
          <div className="md:col-span-8">
            {(() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const upcoming = events
                .filter((ev) => {
                  if (!ev.data_iso) return ev.anno && ev.anno >= today.getFullYear();
                  return new Date(ev.data_iso) >= today;
                })
                .sort((a, b) => new Date(a.data_iso || "2999-01-01") - new Date(b.data_iso || "2999-01-01"))
                .slice(0, 4);

              if (upcoming.length === 0) {
                return (
                  <div className="border border-[#A3133A]/15 bg-[#FBFBF9] p-10 text-center" data-testid="home-events-empty">
                    <p className="text-[#5C5C5C] italic mb-3">Stiamo definendo il prossimo calendario eventi.</p>
                    <p className="text-[12px] tracking-eyebrow text-[#A3133A]">Torna presto a trovarci.</p>
                  </div>
                );
              }

              const months = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

              return (
                <div className="border border-[#A3133A]/15 divide-y divide-[#A3133A]/15">
                  {upcoming.map((ev) => {
                    const d = ev.data_iso ? new Date(ev.data_iso) : null;
                    return (
                      <div key={ev.id} className="bg-[#FBFBF9] p-6 sm:p-8 flex flex-col sm:flex-row gap-5 sm:gap-8 sm:items-center" data-testid={`home-event-${ev.id}`}>
                        <div className="sm:w-28 shrink-0">
                          {d ? (
                            <div className="border border-[#A3133A]/20 inline-block px-4 py-3 text-center bg-white">
                              <div className="text-[10px] tracking-eyebrow text-[#A3133A]">{months[d.getMonth()]}</div>
                              <div className="font-serif-display text-4xl text-[#1C1C1C] leading-none my-1">{d.getDate()}</div>
                              <div className="text-[10px] tracking-eyebrow text-[#5C5C5C]">{d.getFullYear()}</div>
                            </div>
                          ) : (
                            <div className="font-serif-display text-2xl text-[#A3133A]">{ev.anno}</div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] tracking-eyebrow text-[#A3133A] mb-1">{ev.categoria} · {ev.data}</div>
                          <h3 className="font-serif-display text-2xl text-[#1C1C1C] mb-1 leading-tight">{ev.titolo}</h3>
                          <p className="text-sm text-[#5C5C5C]">{ev.luogo}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      {/* TRINITY BANNER */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-[#FBFBF9] border-t border-b border-[#A3133A]/15" data-testid="home-trinity">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-4">Centre Registration {SCHOOL.trinityCentreId}</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1C1C1C] leading-tight mb-6">
              Centro ufficiale<br />
              <em className="italic font-light">Trinity College London.</em>
            </h2>
            <p className="text-[#5C5C5C] leading-relaxed max-w-xl mb-6">
              I nostri allievi possono sostenere gli esami di certificazione
              internazionale Classical & Jazz, Rock & Pop e Theory of Music
              direttamente presso la nostra sede, con esaminatori inviati
              da Londra.
            </p>
            <Link to="/certificazioni" data-testid="home-trinity-cta" className="text-[12px] tracking-eyebrow bg-[#A3133A] text-white px-6 py-3 rounded-sm hover:bg-[#8B0F2F] transition inline-flex items-center gap-2">
              Scopri la certificazione <ArrowRight size={14} />
            </Link>
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/5] bg-[#A3133A] flex items-center justify-center p-8 relative grain overflow-hidden">
              <div className="text-center text-white relative z-10">
                <div className="text-[11px] tracking-eyebrow opacity-80 mb-4">Trinity College London</div>
                <div className="font-serif-display text-6xl mb-3">71607</div>
                <div className="text-[11px] tracking-eyebrow opacity-80">Centre Registration Number</div>
                <div className="w-12 h-px bg-white/40 mx-auto my-6" />
                <div className="font-serif-display italic text-xl">Music · Drama</div>
                <div className="font-serif-display italic text-xl">Performing Arts</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRINITY VOCAL HUB — CORI */}
      <section className="py-20 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto" data-testid="home-vocal-hub">
        <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-4">I Cori · Trinity Vocal Hub · Audizioni sempre aperte</div>
        <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1C1C1C] leading-tight mb-6">
          Un hub, due cori, <em className="italic font-light">una voce sola.</em>
        </h2>
        <p className="text-[#5C5C5C] leading-relaxed max-w-3xl mb-10">
          Dagli 8 anni in su c'è un palco per te: prima prova gratuita, nessuna esperienza richiesta.
          Scegli la formazione che ti assomiglia.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#A3133A]/15 border border-[#A3133A]/15 mb-8">
          <div className="bg-[#FBFBF9] p-8 sm:p-10" data-testid="home-coro-lucincanto">
            <Mic size={26} className="text-[#A3133A] mb-5" />
            <div className="text-[10px] tracking-eyebrow text-[#A3133A] mb-2">Voci Bianche · 8–20 anni · Musical Pop Choir</div>
            <h3 className="font-serif-display text-3xl text-[#1C1C1C] mb-3 leading-tight">LucinCanto</h3>
            <p className="text-sm text-[#5C5C5C] leading-relaxed">
              Energia da musical, armonie pop moderne e palchi veri. Due sezioni:
              Piccole Voci (8–13) e Giovani Armonie (14–20).
            </p>
          </div>
          <div className="bg-[#FBFBF9] p-8 sm:p-10" data-testid="home-coro-apm">
            <Music size={26} className="text-[#A3133A] mb-5" />
            <div className="text-[10px] tracking-eyebrow text-[#A3133A] mb-2">Coro Polifonico · Adulti 18+</div>
            <h3 className="font-serif-display text-3xl text-[#1C1C1C] mb-3 leading-tight">APM Pop Project</h3>
            <p className="text-sm text-[#5C5C5C] leading-relaxed">
              I grandi successi del pop riarrangiati a più voci. Si parte da zero, insieme —
              prove settimanali serali a Rovigo e Polesella.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-4">
          <a
            href="https://www.trinityvocalhub.net"
            target="_blank"
            rel="noreferrer"
            data-testid="home-vocal-hub-cta"
            className="bg-[#A3133A] text-white px-8 py-4 text-[12px] tracking-eyebrow rounded-sm hover:bg-[#8B0F2F] transition inline-flex items-center gap-2"
          >
            Audizioni su trinityvocalhub.net <ArrowRight size={14} />
          </a>
          <Link
            to="/corsi"
            data-testid="home-vocal-hub-corsi"
            className="border border-[#A3133A] text-[#A3133A] px-8 py-4 text-[12px] tracking-eyebrow rounded-sm hover:bg-[#A3133A] hover:text-white transition inline-flex items-center gap-2"
          >
            Tutti i corsi
          </Link>
        </div>
      </section>

      {/* SOSTIENICI BANNER */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-[#A3133A] text-[#FBFBF9] relative grain" data-testid="home-sostienici">
        <div className="max-w-[1400px] mx-auto relative z-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="text-[11px] tracking-eyebrow opacity-80 mb-4">Crowdfunding · Erogazioni liberali</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight mb-5">
              Sostieni <em className="italic font-light">la musica.</em>
            </h2>
            <p className="opacity-90 leading-relaxed max-w-2xl">
              Con una donazione liberale — piccola o grande — aiuti l'associazione a mantenere vive
              le attività didattiche e concertistiche, a rinnovare gli strumenti e a sostenere
              gli allievi più giovani. Importo libero, pagamento sicuro con carta.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
            <Link
              to="/sostienici"
              data-testid="home-sostienici-cta"
              className="bg-[#FBFBF9] text-[#A3133A] px-8 py-4 text-[12px] tracking-eyebrow inline-flex items-center justify-center gap-2 rounded-sm hover:bg-white transition-all"
            >
              <Heart size={14} /> Dona ora — Sostienici
            </Link>
            <Link
              to="/sostienici"
              data-testid="home-sostienici-more"
              className="border border-[#FBFBF9]/50 px-8 py-4 text-[12px] tracking-eyebrow inline-flex items-center justify-center gap-2 rounded-sm hover:bg-white/10 transition-all"
            >
              Scopri il progetto <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* NEWS PREVIEW */}
      <section className="py-24 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto" data-testid="home-news">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
          <div>
            <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-4">Dal Blog</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1C1C1C]">Ultime dalla scuola</h2>
          </div>
          <Link to="/blog" data-testid="home-news-all" className="text-[12px] tracking-eyebrow text-[#A3133A] link-underline">
            Leggi tutto
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {news.slice(0, 3).map((n) => (
            <Link
              to={`/blog/${n.id}`}
              key={n.id}
              data-testid={`home-news-${n.id}`}
              className="group block border border-[#A3133A]/15 hover:border-[#A3133A]/40 transition-all"
            >
              <div className="aspect-[16/10] overflow-hidden bg-[#A3133A]/5">
                <img src={n.immagine} alt={n.titolo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <div className="text-[10px] tracking-eyebrow text-[#A3133A] mb-3">
                  {n.categoria} · {new Date(n.data).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                </div>
                <h3 className="font-serif-display text-2xl text-[#1C1C1C] mb-2 leading-tight">{n.titolo}</h3>
                <p className="text-sm text-[#5C5C5C] line-clamp-2">{n.estratto}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
